import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Shield, Euro, FileText, AlertTriangle, ArrowLeft, Check, Loader2, HandCoins, Copy, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import GarantiesTagsInput from './GarantiesTagsInput'

const API = process.env.REACT_APP_BACKEND_URL

const CONVENTIONS_AMIABLE = [
  { value: 'irsa', label: 'IRSA', forfait: 2030 },
  { value: 'irca', label: 'IRCA', forfait: 1600 },
  { value: 'droit_commun', label: 'Droit commun', forfait: 0 },
]

const CONVENTIONS_ALL = [
  { value: 'irsa', label: 'IRSA' },
  { value: 'irca', label: 'IRCA' },
  { value: 'droit_commun', label: 'Droit commun' },
  { value: 'hors_convention', label: 'Hors convention' },
  { value: 'inconnu', label: 'Inconnu' },
]

const RESPONSABILITES = [
  { value: 'responsable', label: 'Responsable' },
  { value: 'non_responsable', label: 'Non responsable' },
  { value: 'partage', label: 'Partage' },
]

const CANAUX = [
  { value: 'telephone', label: 'Telephone' },
  { value: 'email', label: 'Email' },
  { value: 'courrier', label: 'Courrier' },
  { value: 'visite', label: 'Visite sur site' },
  { value: 'autre', label: 'Autre' },
]

function SectionTitle({ icon, title, num, variant }) {
  const bg = variant === 'amiable' ? 'bg-teal-100 text-teal-600' : 'bg-indigo-100 text-indigo-600'
  return (
    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
      <span className={`w-6 h-6 rounded-full ${bg} flex items-center justify-center text-xs font-bold`}>{num}</span>
      {icon}
      <h3 className="font-semibold text-sm text-slate-800">{title}</h3>
    </div>
  )
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  )
}

const inputCls = "w-full h-9 px-3 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
const selectCls = "w-full h-9 px-3 text-sm border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
const inputAmCls = "w-full h-9 px-3 text-sm border border-teal-200 rounded-lg outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"

export default function PreDeclarationFlotte({ tenantId, policeId, onCreated, onCancel, embedded }) {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [doublons, setDoublons] = useState([])
  const [checkingDoublons, setCheckingDoublons] = useState(false)
  const [doublonConfirme, setDoublonConfirme] = useState(false)

  // Type de sinistre: assurance ou amiable
  const [typeOuverture, setTypeOuverture] = useState('assurance')
  const isAmiable = typeOuverture === 'amiable'

  const [form, setForm] = useState({
    // 1. Identification
    numero_sinistre_client: '',
    date_fait: '',
    immatriculation_saisie: '',
    modele_saisi: '',
    chauffeur_nom_saisi: '',
    chauffeur_prenom_saisi: '',
    nom_chef_depot: '',
    lieu: '',
    // 2. Contexte
    description: '',
    nature_sinistre: '',
    // 3. Tiers
    info_tiers: '',
    immatriculation_tiers: '',
    tiers_nom: '',
    tiers_email: '',
    tiers_telephone: '',
    tiers_assurance: '',
    tiers_vehicule: '',
    // 4. Dommages
    dommages_client: '',
    dommages_tiers: '',
    // 5. Assurance (standard)
    responsabilite_supposee: [],
    convention: '',
    garanties_attendues: [],
    nb_tiers: '',
    // 6. Financier (standard)
    montant_dommages_estime: '',
    montant_devis: '',
    // 7. Eligibilite amiable (standard)
    eligible_amiable: null,
    amiable_propose_tiers: null,
    amiable_motif_refus: '',
    // Amiable specific fields
    convention_amiable: '',
    canal_reception_amiable: '',
    montant_amiable_ht: '',
    montant_amiable_ttc: '',
    nom_garage: '',
    societe_proprietaire: '',
    commentaire_montant_amiable: '',
  })

  function set(field, value) { setForm(f => ({ ...f, [field]: value })) }

  // Detection doublons (immat / chauffeur / lieu, date ±3 jours)
  const doublonKey = [form.date_fait, form.immatriculation_saisie, form.chauffeur_nom_saisi, form.chauffeur_prenom_saisi, form.lieu].join('|')
  useEffect(() => {
    setDoublonConfirme(false)
    if (!tenantId || !form.date_fait || !(form.immatriculation_saisie || form.chauffeur_nom_saisi || form.lieu)) { setDoublons([]); return }
    const t = setTimeout(async () => {
      setCheckingDoublons(true)
      try {
        const res = await fetch(`${API}/api/sinistres/detecter-doublons`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tenant_id: tenantId, date_fait: form.date_fait,
            immatriculation: form.immatriculation_saisie || null,
            chauffeur_nom: form.chauffeur_nom_saisi || null,
            chauffeur_prenom: form.chauffeur_prenom_saisi || null,
            lieu: form.lieu || null,
          }),
        })
        const d = await res.json()
        setDoublons(d.doublons || [])
      } catch { setDoublons([]) }
      finally { setCheckingDoublons(false) }
    }, 600)
    return () => clearTimeout(t)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, doublonKey])

  async function handleSubmit() {
    if (!canSubmit) {
      toast.error(`Champs obligatoires manquants : ${errors.join(', ')}`)
      return
    }
    if (doublons.length > 0 && !doublonConfirme) {
      toast.warning('Doublon suspecté : confirmez l\'ouverture malgré tout avant de créer le dossier')
      return
    }
    setSaving(true)
    try {
      const convAm = CONVENTIONS_AMIABLE.find(c => c.value === form.convention_amiable)

      const payload = {
        tenant_id: tenantId,
        type_sinistre: 'flotte',
        nature_sinistre: form.nature_sinistre || 'Sinistre flotte',
        description: form.description || (isAmiable ? 'Pre-declaration amiable' : 'Pre-declaration flotte'),
        date_fait: form.date_fait || new Date().toISOString().split('T')[0],
        police_id: policeId || null,
        immatriculation_saisie: form.immatriculation_saisie ? form.immatriculation_saisie.replace(/[\s-]/g, '').toUpperCase() : null,
        modele_saisi: form.modele_saisi || null,
        chauffeur_nom_saisi: form.chauffeur_nom_saisi.trim() || null,
        chauffeur_prenom_saisi: form.chauffeur_prenom_saisi.trim() || null,
        chauffeur_non_reference: true,
        nom_chef_depot: form.nom_chef_depot || null,
        lieu: form.lieu || null,
        doublon_confirme: doublons.length > 0 ? doublonConfirme : false,
        doublons_suspects: doublons.length > 0 ? doublons.map(d => ({ id: d.id, numero_sinistre: d.numero_sinistre, numero_sinistre_client: d.numero_sinistre_client, motifs: d.motifs })) : null,
        numero_sinistre_client: form.numero_sinistre_client,
        info_tiers: form.info_tiers || null,
        immatriculation_tiers: form.immatriculation_tiers ? form.immatriculation_tiers.replace(/-/g, '') : null,
        tiers_nom: form.tiers_nom || null,
        tiers_email: form.tiers_email || null,
        tiers_telephone: form.tiers_telephone || null,
        tiers_assurance: form.tiers_assurance || null,
        tiers_vehicule: form.tiers_vehicule || null,
        dommages_client: form.dommages_client || null,
        dommages_tiers: form.dommages_tiers || null,
        responsabilite_supposee: form.responsabilite_supposee.length > 0 ? form.responsabilite_supposee : null,
        responsabilite: form.responsabilite_supposee.length === 1 ? form.responsabilite_supposee[0] : null,
        mode_gestion: isAmiable ? 'amiable' : 'assurance',
        est_amiable: isAmiable,
      }

      if (isAmiable) {
        // Amiable-specific payload
        payload.convention = form.convention_amiable || null
        payload.canal_reception_amiable = form.canal_reception_amiable || null
        payload.montant_amiable_ht = form.montant_amiable_ht ? parseFloat(form.montant_amiable_ht) : null
        payload.montant_amiable_ttc = form.montant_amiable_ttc ? parseFloat(form.montant_amiable_ttc) : null
        payload.nom_garage = form.nom_garage || null
        payload.societe_proprietaire = form.societe_proprietaire || null
        payload.commentaire_montant_amiable = form.commentaire_montant_amiable || null
        payload.reglement_amiable_type = form.convention_amiable === 'droit_commun' ? null : (form.convention_amiable || '').toUpperCase()
        // Set forfait if IRSA/IRCA
        if (convAm && convAm.forfait > 0) {
          payload.forfait_irsa = convAm.forfait
          // Compute economy immediately if montant HT is provided
          if (payload.montant_amiable_ht) {
            payload.economie_amiable = convAm.forfait - payload.montant_amiable_ht
          }
        }
      } else {
        // Standard payload
        payload.convention = form.convention || null
        payload.garanties_attendues = form.garanties_attendues.length > 0 ? form.garanties_attendues : null
        payload.nb_tiers = form.nb_tiers !== '' ? parseInt(form.nb_tiers, 10) : null
        payload.montant_dommages_estime = form.montant_dommages_estime ? parseFloat(form.montant_dommages_estime) : null
        payload.montant_devis = form.montant_devis ? parseFloat(form.montant_devis) : null
        payload.eligible_amiable = form.eligible_amiable
        payload.amiable_propose_tiers = form.amiable_propose_tiers
        payload.amiable_motif_refus = form.amiable_motif_refus || null
      }

      const res = await fetch(`${API}/api/sinistres`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (data.success || data.sinistre) {
        toast.success(isAmiable ? 'Sinistre amiable cree' : 'Sinistre flotte cree')
        const newId = data.sinistre?.id || data.id
        if (onCreated) onCreated(newId)
        else if (newId) navigate(`/c2/sinistre/${newId}`)
      } else {
        toast.error(data.detail || 'Erreur creation')
      }
    } catch (err) {
      toast.error('Erreur de creation')
    } finally {
      setSaving(false)
    }
  }

  // Validation relaxed — gestionnaire decides if ready to submit
  const errors = []
  const canSubmit = true

  return (
    <div className="max-w-3xl mx-auto" data-testid="pre-declaration-flotte">
      {!embedded && (
        <div className="flex items-center gap-3 mb-6">
          <button onClick={onCancel} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-testid="predecl-cancel">
            <ArrowLeft size={18} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Pre-declaration sinistre flotte</h1>
            <p className="text-xs text-slate-500">Remplissez les informations disponibles en une seule saisie</p>
          </div>
        </div>
      )}

      <div className="space-y-6">

        {/* ── TYPE D'OUVERTURE (Assurance / Amiable) ── */}
        <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="type-ouverture-block">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
            <Shield size={16} className="text-indigo-500" />
            <h3 className="font-semibold text-sm text-slate-800">Type d'ouverture</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setTypeOuverture('assurance')}
              className={`px-4 py-3 rounded-xl border-2 text-left transition-all ${
                !isAmiable
                  ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-300'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid="type-assurance-btn"
            >
              <p className={`text-sm font-bold ${!isAmiable ? 'text-indigo-800' : 'text-slate-700'}`}>Assurance</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Ouverture classique avec declaration compagnie</p>
            </button>
            <button
              onClick={() => setTypeOuverture('amiable')}
              className={`px-4 py-3 rounded-xl border-2 text-left transition-all ${
                isAmiable
                  ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-300'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid="type-amiable-btn"
            >
              <p className={`text-sm font-bold ${isAmiable ? 'text-teal-800' : 'text-slate-700'}`}>Amiable</p>
              <p className="text-[10px] text-slate-500 mt-0.5">Reglement direct sans passer par l'assureur</p>
            </button>
          </div>
        </div>

        {/* 1. Identification */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <SectionTitle icon={<FileText size={16} className="text-indigo-500" />} title="Identification" num="1" variant={isAmiable ? 'amiable' : undefined} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="N sinistre client">
              <input type="text" value={form.numero_sinistre_client} onChange={e => set('numero_sinistre_client', e.target.value)}
                className={inputCls} placeholder="CLT-2025-..." data-testid="field-numero-client" />
            </Field>
            <Field label="Date du sinistre">
              <input type="date" value={form.date_fait} onChange={e => set('date_fait', e.target.value)} className={inputCls} data-testid="field-date-fait" />
            </Field>
            <Field label="Immatriculation">
              <input type="text" value={form.immatriculation_saisie} onChange={e => set('immatriculation_saisie', e.target.value.toUpperCase())}
                className={`${inputCls} font-mono`} placeholder="AB-123-CD" data-testid="field-immatriculation" />
            </Field>
            <Field label="Marque et modele">
              <input type="text" value={form.modele_saisi} onChange={e => set('modele_saisi', e.target.value)}
                className={inputCls} placeholder="Mercedes Actros..." data-testid="field-modele" />
            </Field>
            <Field label="Chauffeur — Nom">
              <input type="text" value={form.chauffeur_nom_saisi} onChange={e => set('chauffeur_nom_saisi', e.target.value)}
                className={inputCls} placeholder="DUPONT" data-testid="field-chauffeur-nom" />
            </Field>
            <Field label="Chauffeur — Prenom">
              <input type="text" value={form.chauffeur_prenom_saisi} onChange={e => set('chauffeur_prenom_saisi', e.target.value)}
                className={inputCls} placeholder="Jean" data-testid="field-chauffeur-prenom" />
            </Field>
            <Field label="Depot">
              <input type="text" value={form.nom_chef_depot} onChange={e => set('nom_chef_depot', e.target.value)} className={inputCls} data-testid="field-depot" />
            </Field>
            <Field label="Lieu du sinistre">
              <input type="text" value={form.lieu} onChange={e => set('lieu', e.target.value)} className={inputCls} placeholder="Ville, rue, autoroute..." data-testid="field-lieu" />
            </Field>
          </div>
        </div>

        {/* Alerte doublon */}
        {(doublons.length > 0 || checkingDoublons) && (
          <div className={`rounded-xl border-2 p-4 ${doublonConfirme ? 'border-slate-200 bg-slate-50' : 'border-amber-400 bg-amber-50'}`} data-testid="doublon-alert">
            {checkingDoublons && doublons.length === 0 ? (
              <p className="text-xs text-slate-500 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Vérification des doublons...</p>
            ) : (
              <>
                <div className="flex items-start gap-2">
                  <Copy size={16} className={`shrink-0 mt-0.5 ${doublonConfirme ? 'text-slate-400' : 'text-amber-600'}`} />
                  <div className="flex-1">
                    <p className={`text-sm font-bold ${doublonConfirme ? 'text-slate-600' : 'text-amber-800'}`}>
                      Ce dossier semble être un doublon ({doublons.length} dossier{doublons.length > 1 ? 's' : ''} similaire{doublons.length > 1 ? 's' : ''} à ±3 jours)
                    </p>
                    <ul className="mt-2 space-y-1.5">
                      {doublons.map(d => (
                        <li key={d.id} className="flex items-center justify-between gap-2 bg-white/70 border border-amber-200 rounded-lg px-3 py-1.5 text-xs" data-testid={`doublon-item-${d.id}`}>
                          <div className="min-w-0">
                            <span className="font-mono font-bold text-indigo-700">{d.numero_sinistre_client || d.numero_sinistre}</span>
                            <span className="text-slate-500 ml-2">{d.date_fait ? new Date(d.date_fait).toLocaleDateString('fr-FR') : ''}{d.immatriculation ? ` · ${d.immatriculation}` : ''}{d.chauffeur ? ` · ${d.chauffeur}` : ''}</span>
                            <div className="flex gap-1 mt-0.5">
                              {d.motifs.map(m => <span key={m} className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-medium">{m}</span>)}
                            </div>
                          </div>
                          <a href={`/c2/sinistre/${d.id}`} target="_blank" rel="noreferrer" className="shrink-0 text-indigo-600 hover:underline flex items-center gap-1" data-testid={`doublon-link-${d.id}`}>
                            Ouvrir <ExternalLink size={11} />
                          </a>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex items-center gap-3">
                      {!doublonConfirme ? (
                        <>
                          <button type="button" onClick={() => setDoublonConfirme(true)}
                            className="px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-lg hover:bg-amber-700" data-testid="doublon-confirm-btn">
                            Confirmer l'ouverture malgré tout
                          </button>
                          <button type="button" onClick={onCancel} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-white rounded-lg" data-testid="doublon-abandon-btn">
                            Abandonner la saisie
                          </button>
                        </>
                      ) : (
                        <p className="text-xs text-slate-600 flex items-center gap-1"><Check size={12} className="text-emerald-500" /> Ouverture confirmée — le dossier sera marqué « doublon confirmé par le gestionnaire ».</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* 2. Contexte */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <SectionTitle icon={<FileText size={16} className="text-slate-500" />} title="Contexte" num="2" variant={isAmiable ? 'amiable' : undefined} />
          <div className="space-y-4">
            <Field label="Description / Circonstances">
              <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={5}
                className="w-full text-sm border border-slate-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                placeholder="Decrivez les faits, circonstances et nature du sinistre..." data-testid="field-description" />
            </Field>
          </div>
        </div>

        {/* 3. Tiers */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <SectionTitle icon={<User size={16} className="text-orange-500" />} title="Tiers" num="3" variant={isAmiable ? 'amiable' : undefined} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nom et prenom">
              <input type="text" value={form.tiers_nom} onChange={e => set('tiers_nom', e.target.value)} className={isAmiable ? inputAmCls : inputCls} placeholder="Nom Prenom du tiers" data-testid="field-tiers-nom" />
            </Field>
            <Field label="Adresse e-mail">
              <input type="email" value={form.tiers_email} onChange={e => set('tiers_email', e.target.value)} className={isAmiable ? inputAmCls : inputCls} placeholder="email@tiers.fr" data-testid="field-tiers-email" />
            </Field>
            <Field label="Telephone">
              <input type="tel" value={form.tiers_telephone} onChange={e => set('tiers_telephone', e.target.value)} className={isAmiable ? inputAmCls : inputCls} placeholder="06 ..." data-testid="field-tiers-telephone" />
            </Field>
            <Field label="Compagnie d'assurance">
              <input type="text" value={form.tiers_assurance} onChange={e => set('tiers_assurance', e.target.value)} className={isAmiable ? inputAmCls : inputCls} placeholder="Nom assureur tiers" data-testid="field-tiers-assurance" />
            </Field>
            <Field label="Immatriculation tiers">
              <input type="text" value={form.immatriculation_tiers} onChange={e => set('immatriculation_tiers', e.target.value)} className={isAmiable ? inputAmCls : inputCls} data-testid="field-immat-tiers" />
            </Field>
            <Field label="Marque et modele">
              <input type="text" value={form.tiers_vehicule} onChange={e => set('tiers_vehicule', e.target.value)} className={isAmiable ? inputAmCls : inputCls} placeholder="Renault Clio..." data-testid="field-tiers-vehicule" />
            </Field>
            <div className="col-span-2">
              <Field label="Informations complementaires tiers">
                <textarea value={form.info_tiers} onChange={e => set('info_tiers', e.target.value)} rows={2}
                  className={`w-full text-sm border rounded-lg p-3 outline-none focus:ring-2 resize-none ${isAmiable ? 'border-teal-200 focus:ring-teal-500' : 'border-slate-200 focus:ring-indigo-500'}`}
                  placeholder="Autres infos utiles..." data-testid="field-info-tiers" />
              </Field>
            </div>
          </div>
        </div>

        {/* 4. Dommages */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <SectionTitle icon={<AlertTriangle size={16} className="text-amber-500" />} title="Dommages" num="4" variant={isAmiable ? 'amiable' : undefined} />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Dommages vehicule client">
              <textarea value={form.dommages_client} onChange={e => set('dommages_client', e.target.value)} rows={2}
                className="w-full text-sm border border-slate-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-indigo-500 resize-none" data-testid="field-dommages-client" />
            </Field>
            <Field label="Dommages vehicule tiers">
              <textarea value={form.dommages_tiers} onChange={e => set('dommages_tiers', e.target.value)} rows={2}
                className="w-full text-sm border border-slate-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-indigo-500 resize-none" data-testid="field-dommages-tiers" />
            </Field>
          </div>
        </div>

        {/* ═══ SECTIONS CONDITIONNELLES ═══ */}

        {/* === MODE AMIABLE === */}
        {isAmiable && (
          <>
            {/* 5. Convention amiable */}
            <div className="bg-teal-50/30 rounded-xl border-2 border-teal-200 p-5" data-testid="section-convention-amiable">
              <SectionTitle icon={<HandCoins size={16} className="text-teal-600" />} title="Convention amiable" num="5" variant="amiable" />
              <div className="grid grid-cols-3 gap-3">
                {CONVENTIONS_AMIABLE.map(c => {
                  const sel = form.convention_amiable === c.value
                  return (
                    <button key={c.value} onClick={() => set('convention_amiable', c.value)}
                      className={`px-3 py-3 rounded-xl border-2 text-left transition-all ${
                        sel ? 'border-teal-500 bg-teal-100 ring-1 ring-teal-400' : 'border-slate-200 bg-white hover:border-teal-300'
                      }`}
                      data-testid={`conv-${c.value}-btn`}
                    >
                      <p className={`text-sm font-bold ${sel ? 'text-teal-800' : 'text-slate-700'}`}>{c.label}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {c.forfait > 0 ? `Forfait ${c.forfait} EUR` : 'Pas de forfait'}
                      </p>
                    </button>
                  )
                })}
              </div>
              {form.convention_amiable === 'droit_commun' && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3 flex items-center gap-1.5">
                  <AlertTriangle size={12} />
                  Droit commun : pas de convention donc pas d'economie amiable comptabilisee
                </p>
              )}
            </div>

            {/* 6. Informations financieres amiable */}
            <div className="bg-teal-50/30 rounded-xl border-2 border-teal-200 p-5" data-testid="section-financier-amiable">
              <SectionTitle icon={<Euro size={16} className="text-teal-600" />} title="Informations financieres" num="6" variant="amiable" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Montant HT">
                  <input type="number" step="0.01" value={form.montant_amiable_ht} onChange={e => set('montant_amiable_ht', e.target.value)}
                    className={inputAmCls} placeholder="0.00" data-testid="field-montant-ht-am" />
                </Field>
                <Field label="Montant TTC">
                  <input type="number" step="0.01" value={form.montant_amiable_ttc} onChange={e => set('montant_amiable_ttc', e.target.value)}
                    className={inputAmCls} placeholder="0.00" data-testid="field-montant-ttc-am" />
                </Field>
                <Field label="Canal de reception">
                  <select value={form.canal_reception_amiable} onChange={e => set('canal_reception_amiable', e.target.value)}
                    className={selectCls} data-testid="field-canal-am">
                    <option value="">Selectionner...</option>
                    {CANAUX.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Nom du garage">
                  <input type="text" value={form.nom_garage} onChange={e => set('nom_garage', e.target.value)}
                    className={inputAmCls} placeholder="Garage..." data-testid="field-garage-am" />
                </Field>
                <Field label="Societe proprietaire du vehicule">
                  <input type="text" value={form.societe_proprietaire} onChange={e => set('societe_proprietaire', e.target.value)}
                    className={inputAmCls} placeholder="Societe..." data-testid="field-societe-am" />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Commentaire financier">
                  <textarea value={form.commentaire_montant_amiable} onChange={e => set('commentaire_montant_amiable', e.target.value)} rows={2}
                    className="w-full text-sm border border-teal-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                    placeholder="Details sur les montants..." data-testid="field-commentaire-am" />
                </Field>
              </div>
            </div>
          </>
        )}

        {/* === MODE ASSURANCE === */}
        {!isAmiable && (
          <>
            {/* 5. Assurance */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <SectionTitle icon={<Shield size={16} className="text-blue-500" />} title="Assurance" num="5" />
              <div className="grid grid-cols-3 gap-4">
                <Field label="Responsabilite supposee">
                  <div className="space-y-1.5" data-testid="field-responsabilite-supposee">
                    {RESPONSABILITES.map(r => {
                      const checked = form.responsabilite_supposee.includes(r.value)
                      return (
                        <label key={r.value} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs cursor-pointer border transition-all ${
                          checked ? 'bg-indigo-50 border-indigo-300 text-indigo-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}>
                          <input type="checkbox" checked={checked}
                            onChange={() => {
                              const next = checked
                                ? form.responsabilite_supposee.filter(v => v !== r.value)
                                : [...form.responsabilite_supposee, r.value]
                              set('responsabilite_supposee', next)
                            }}
                            className="rounded border-slate-300 text-indigo-600 w-3.5 h-3.5"
                          />
                          {r.label}
                        </label>
                      )
                    })}
                  </div>
                </Field>
                <Field label="Convention">
                  <select value={form.convention} onChange={e => set('convention', e.target.value)} className={selectCls} data-testid="field-convention">
                    <option value="">Selectionner...</option>
                    {CONVENTIONS_ALL.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Nombre de tiers">
                  <input type="number" min="0" step="1" value={form.nb_tiers} onChange={e => set('nb_tiers', e.target.value)} className={inputCls} placeholder="0" data-testid="field-nb-tiers" />
                </Field>
              </div>
              <div className="mt-4">
                <Field label="Garanties attendues (saisie libre)">
                  <GarantiesTagsInput value={form.garanties_attendues} onChange={v => set('garanties_attendues', v)} tenantId={tenantId} testId="field-garanties-attendues" />
                  <p className="text-[10px] text-slate-400 mt-1">Tapez un libellé puis Entrée — comparé au relevé compagnie lors du contrôle.</p>
                </Field>
              </div>
            </div>

            {/* 6. Financier */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <SectionTitle icon={<Euro size={16} className="text-emerald-500" />} title="Financier initial" num="6" />
              <div className="grid grid-cols-2 gap-4">
                <Field label="Estimation dommages">
                  <input type="number" step="0.01" value={form.montant_dommages_estime} onChange={e => set('montant_dommages_estime', e.target.value)} className={inputCls} placeholder="0.00" data-testid="field-estimation" />
                </Field>
                <Field label="Devis (si disponible)">
                  <input type="number" step="0.01" value={form.montant_devis} onChange={e => set('montant_devis', e.target.value)} className={inputCls} placeholder="0.00" data-testid="field-devis" />
                </Field>
              </div>
            </div>

            {/* 7. Eligibilite amiable */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <SectionTitle icon={<Shield size={16} className="text-teal-500" />} title="Eligibilite amiable" num="7" />
              <div className="space-y-4">
                <Field label="Le sinistre est-il eligible a un traitement amiable ?">
                  <div className="flex gap-3" data-testid="field-eligible-amiable">
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm cursor-pointer transition-all ${
                      form.eligible_amiable === true ? 'bg-teal-50 border-teal-300 text-teal-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}>
                      <input type="radio" name="eligible_amiable" checked={form.eligible_amiable === true} onChange={() => set('eligible_amiable', true)} className="hidden" />
                      OUI
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm cursor-pointer transition-all ${
                      form.eligible_amiable === false ? 'bg-red-50 border-red-300 text-red-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}>
                      <input type="radio" name="eligible_amiable" checked={form.eligible_amiable === false} onChange={() => set('eligible_amiable', false)} className="hidden" />
                      NON
                    </label>
                  </div>
                </Field>

                {form.eligible_amiable === true && (
                  <Field label="Le processus amiable a-t-il ete propose au tiers ?">
                    <div className="flex gap-3" data-testid="field-amiable-propose">
                      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm cursor-pointer transition-all ${
                        form.amiable_propose_tiers === true ? 'bg-teal-50 border-teal-300 text-teal-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}>
                        <input type="radio" name="amiable_propose" checked={form.amiable_propose_tiers === true} onChange={() => set('amiable_propose_tiers', true)} className="hidden" />
                        OUI
                      </label>
                      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm cursor-pointer transition-all ${
                        form.amiable_propose_tiers === false ? 'bg-red-50 border-red-300 text-red-700 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}>
                        <input type="radio" name="amiable_propose" checked={form.amiable_propose_tiers === false} onChange={() => set('amiable_propose_tiers', false)} className="hidden" />
                        NON
                      </label>
                    </div>
                  </Field>
                )}

                {(form.eligible_amiable === false || form.amiable_propose_tiers === false) && (
                  <Field label="Motif">
                    <textarea value={form.amiable_motif_refus} onChange={e => set('amiable_motif_refus', e.target.value)} rows={2}
                      className="w-full text-sm border border-slate-200 rounded-lg p-3 outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                      placeholder="Indiquer le motif du refus ou de non-eligibilite..."
                      data-testid="field-amiable-motif" />
                  </Field>
                )}
              </div>
            </div>
          </>
        )}

        {/* Submit */}
        <div className={`flex items-center justify-between rounded-xl border p-5 ${
          isAmiable ? 'bg-teal-50/30 border-teal-200' : 'bg-white border-slate-200'
        }`}>
          <div>
            {errors.length > 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle size={12} />
                Champs requis : {errors.join(', ')}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors" data-testid="predecl-cancel-btn">
              Annuler
            </button>
            <button onClick={handleSubmit} disabled={saving || !canSubmit || (doublons.length > 0 && !doublonConfirme)}
              title={doublons.length > 0 && !doublonConfirme ? 'Confirmez d\'abord l\'ouverture malgré le doublon suspecté' : undefined}
              className={`px-6 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors ${
                isAmiable ? 'bg-teal-600 hover:bg-teal-700' : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
              data-testid="predecl-submit-btn">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Creation...</> : <><Check size={14} /> {isAmiable ? 'Creer sinistre amiable' : 'Creer le sinistre'}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
